import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { RiderPayrollFormValues } from "./rider-payroll-schema";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

interface RiderPayrollAdjustmentsTableProps {
  form: UseFormReturn<RiderPayrollFormValues>;
  fields: FieldArrayWithId<RiderPayrollFormValues, "adjustments", "id">[];
  onAddAdjustment: () => void;
  onRemoveAdjustment: (index: number) => void;
  readOnly: boolean;
}

export function RiderPayrollAdjustmentsTable({
  form,
  fields,
  onAddAdjustment,
  onRemoveAdjustment,
  readOnly,
}: RiderPayrollAdjustmentsTableProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <FormLabel>Penyesuaian (Kasbon, Kekurangan Setoran, dll)</FormLabel>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={onAddAdjustment}
          >
            <Plus className="h-3.5 w-3.5" />
            Tambah
          </Button>
        )}
      </div>

      {fields.length === 0 ? (
        <p className="py-1 text-xs text-muted-foreground">
          Belum ada penyesuaian.
        </p>
      ) : (
        <div className="space-y-2 max-h-52 overflow-y-auto rounded-md border p-2">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-2.5 sm:flex-row sm:items-center sm:gap-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0"
            >
              <FormField
                control={form.control}
                name={`adjustments.${index}.type`}
                render={({ field: f }) => (
                  <FormItem className="sm:w-32">
                    <Select
                      value={f.value}
                      onValueChange={f.onChange}
                      disabled={readOnly}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="income">Pendapatan</SelectItem>
                        <SelectItem value="deduction">Potongan</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`adjustments.${index}.label`}
                render={({ field: f }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <input
                        type="text"
                        placeholder="Contoh: Kasbon"
                        disabled={readOnly}
                        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-base shadow-xs outline-none sm:text-sm"
                        {...f}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`adjustments.${index}.amount`}
                render={({ field: f }) => (
                  <FormItem className="sm:w-32">
                    <FormControl>
                      <NumberInput
                        placeholder="0"
                        disabled={readOnly}
                        className="h-9 text-right"
                        value={f.value ?? 0}
                        onChange={f.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => onRemoveAdjustment(index)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
